/**
 * SharePostModal — web port of the mobile share bottom sheet.
 *
 * Members can reshare directly inside WLB with an optional message. Direct
 * external destinations stay in this modal instead of opening a second,
 * operating-system share chooser.
 */
import { useContext, useEffect, useRef, useState } from 'react';
import groupContext from '@wlb/shared/context/GroupContext';
import messageContext from '@wlb/shared/context/MessageContext';
import { useFeed } from '@wlb/shared/context/FeedContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { POST_MAX_CHAR_COUNT, POST_PRIVACY } from '@wlb/shared/constants';
import { buildSharePostUrl } from '@wlb/shared/config/env';
import { ToastService } from '@wlb/shared/platform/notifier';
import { copyWithToast } from '@wlb/shared/utils/clipboardHelper';
import { canSharePost } from '@wlb/shared/utils/postSharing';
import type { FeedPost } from '@wlb/shared/types/post';
import type { PostPrivacy } from '@wlb/shared/types';
import type { ConversationMember } from '@wlb/shared/types/chat';
import {
  mapPrivacyToVisibilityInt,
  mapVisibilityIntToPrivacy,
} from '@wlb/shared/api/services/feedService';
import { Modal } from '../../components/ui/Modal';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { EmojiPicker } from '../../components/ui/EmojiPicker';
import { FormError } from '../../components/ui/FormError';
import {
  BuddiesIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FacebookIcon,
  GroupsTabIcon,
  GlobeIcon,
  InstagramIcon,
  LinkIcon,
  LockIcon,
  MailIcon,
  MessagesIcon,
  ShareIcon,
  SmileIcon,
  XIcon,
} from '../../components/ui/icons';
import { useCurrentProfile } from '../profile/useCurrentProfile';
import { normalizePostDisplayText } from './postTextPreview';
import {
  buildExternalShareLinks,
  buildInAppShareBody,
  buildInAppShareRequest,
  buildInternalPostUrl,
  buildPrivateShareMessage,
  canQuickReshare,
} from './sharePostRequest';
import {
  buildReshareLineageUrl,
  extractReshareLineage,
  extractReshareSourcePostId,
} from '../../utils/reshareAttribution';
import { resolvePostAlbum } from './postMedia';
import { formatPostFeeling } from './postFeelingDisplay';
import './feedModal.css';

interface SharePostModalProps {
  post: FeedPost;
  onClose: () => void;
  onShared?: () => void;
}

type SharePicker = 'group' | 'friend' | null;

const PRIVACY_OPTIONS: PostPrivacy[] = [
  POST_PRIVACY.PUBLIC,
  POST_PRIVACY.BUDDIES_ONLY,
  POST_PRIVACY.ONLY_ME,
];

const PrivacyIcon = ({ privacy }: { privacy: PostPrivacy }) => {
  if (privacy === POST_PRIVACY.PUBLIC) return <GlobeIcon size={16} />;
  if (privacy === POST_PRIVACY.BUDDIES_ONLY) return <BuddiesIcon size={16} />;
  return <LockIcon size={16} />;
};

export function SharePostModal({ post, onClose, onShared }: SharePostModalProps) {
  const { t } = useTranslation();
  const { sharePost, requestFeedRefresh } = useFeed();
  const group = useContext(groupContext);
  const messages = useContext(messageContext);
  const profile = useCurrentProfile();
  const [message, setMessage] = useState('');
  const [privacy, setPrivacy] = useState<PostPrivacy>(
    post.privacy
      ?? mapVisibilityIntToPrivacy(post.postVisibility)
      ?? POST_PRIVACY.PUBLIC,
  );
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [inAppSharing, setInAppSharing] = useState(false);
  const [shareError, setShareError] = useState('');
  const [picker, setPicker] = useState<SharePicker>(null);
  const [friendCandidates, setFriendCandidates] = useState<ConversationMember[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [buddiesLoading, setBuddiesLoading] = useState(Boolean(messages));
  const buddiesLoadStartedRef = useRef(false);
  const shareOptionsRef = useRef<HTMLDivElement>(null);
  const [canScrollShareOptionsLeft, setCanScrollShareOptionsLeft] = useState(false);
  const [canScrollShareOptionsRight, setCanScrollShareOptionsRight] = useState(true);

  useEffect(() => {
    if (!messages || buddiesLoadStartedRef.current) {
      setBuddiesLoading(false);
      return;
    }

    buddiesLoadStartedRef.current = true;
    let active = true;
    messages.fetchBuddyCandidates()
      .then(candidates => {
        if (active) setFriendCandidates(candidates);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setBuddiesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [messages]);

  useEffect(() => {
    const options = shareOptionsRef.current;
    if (!options) return;

    const updateOverflow = () => {
      const remaining = options.scrollWidth - options.clientWidth - options.scrollLeft;
      setCanScrollShareOptionsLeft(options.scrollLeft > 4);
      setCanScrollShareOptionsRight(remaining > 4);
    };

    updateOverflow();
    window.addEventListener('resize', updateOverflow);
    return () => window.removeEventListener('resize', updateOverflow);
  }, []);

  if (!canSharePost(post)) {
    return null;
  }

  const existingLineage = extractReshareLineage(post.text);
  const rootPostId = extractReshareSourcePostId(existingLineage) || post.id;
  const shareUrl = buildSharePostUrl(rootPostId);
  const privateShareUrl = buildInternalPostUrl(rootPostId, window.location.origin);
  const canReshareInsideWlb = canQuickReshare(post);
  const busy = inAppSharing;
  const profileName =
    profile?.userName || profile?.name || t('bottomSheet.currentMember');
  const privacyLabel =
    privacy === POST_PRIVACY.BUDDIES_ONLY
      ? t('home.createPost.privacy.buddiesOnly')
      : privacy === POST_PRIVACY.ONLY_ME
        ? t('home.createPost.privacy.onlyMe')
        : t('home.createPost.privacy.public');
  const originalVisibility = mapPrivacyToVisibilityInt(
    post.privacy ?? post.postVisibility,
  );
  const privacyOptions = PRIVACY_OPTIONS.filter(
    option => mapPrivacyToVisibilityInt(option) >= originalVisibility,
  );
  const originalAuthor = existingLineage?.originalAuthor ?? {
    userGuid: post.profileUserGuid,
    username: post.username,
  };
  const originalPostText = normalizePostDisplayText(
    existingLineage?.originalText || post.text,
  );
  const originalPostImages = resolvePostAlbum(post);
  const originalPostImage = originalPostImages[0] ?? null;
  const originalPostVideo = post.video?.uri ?? post.videoUrl ?? null;
  const originalPostFeeling = formatPostFeeling(post.feeling, t);
  const externalShareText = t('bottomSheet.sharePostTemplate', {
    username: post.username,
    message: originalPostText || t('bottomSheet.fallbackShareMessage'),
  });
  const nativeShareText = `${externalShareText}\n\n${shareUrl}`;
  const privateShareText = [
    t('bottomSheet.sharedFrom', { username: post.username }),
    originalPostText,
  ].filter(Boolean).join('\n');
  const externalLinks = buildExternalShareLinks({
    shareUrl,
    text: externalShareText,
    subject: t('bottomSheet.nativeShareTitle'),
  });

  const copyLink = () => {
    copyWithToast(shareUrl, t('profile.toast.copied'));
    onClose();
  };

  const openNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('bottomSheet.nativeShareTitle'),
          text: nativeShareText,
          url: shareUrl,
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        copyLink();
      }
      return;
    }

    copyLink();
  };

  const buildReshareBody = () =>
    buildInAppShareBody({
      message,
      originalText: originalPostText,
      shareUrl: buildReshareLineageUrl(
        existingLineage?.sourceUrl || shareUrl,
        originalAuthor,
        [],
      ),
      attribution: t('bottomSheet.originalPostBy', {
        username: `@${originalAuthor.username}`,
      }),
    });

  const handleShareInsideApp = async (destinationGroupId?: string) => {
    if (busy || !canReshareInsideWlb) return;

    setShowEmojiPicker(false);
    setPrivacyOpen(false);
    setInAppSharing(true);
    setShareError('');
    const request = buildInAppShareRequest(
      {
        id: rootPostId,
        groupId: destinationGroupId ?? post.groupId,
      },
      buildReshareBody(),
      mapPrivacyToVisibilityInt(privacy),
    );
    const result = await sharePost(
      request.postId,
      request.bodyText,
      request.groupId,
      request.privacyId,
    );

    if (!result.success) {
      setShareError(t('bottomSheet.inAppShareFailed'));
      setInAppSharing(false);
      return;
    }

    if (post.groupId) {
      group?.updateGroupShareCount(post.id);
    }
    requestFeedRefresh();
    ToastService.show({
      type: 'success',
      message: t('bottomSheet.inAppShareSuccess'),
    });
    onShared?.();
    setInAppSharing(false);
    onClose();
  };

  const openGroupPicker = async () => {
    if (!canReshareInsideWlb) return;
    setPicker('group');
    setPickerLoading(true);
    setShareError('');
    await group?.fetchMyGroups(undefined, { force: true });
    setPickerLoading(false);
  };

  const openFriendPicker = async () => {
    setPicker('friend');
    if (friendCandidates.length > 0) return;
    setPickerLoading(true);
    setShareError('');
    try {
      setFriendCandidates(await messages?.fetchBuddyCandidates() ?? []);
    } catch {
      setShareError(t('bottomSheet.friendShareFailed'));
    } finally {
      setPickerLoading(false);
    }
  };

  const shareWithFriend = async (friend: ConversationMember) => {
    if (!messages || busy) return;
    const friendId = Number(friend.id);
    if (!Number.isFinite(friendId) || friendId <= 0) return;

    setInAppSharing(true);
    setShareError('');
    try {
      const conversation = await messages.openOrCreateDirectConversation({
        userId: friendId,
        name: friend.name,
        username: friend.username,
        avatar: typeof friend.avatar === 'string' ? friend.avatar : null,
      });
      const result = await messages.sendMessage(conversation.id, {
        text: buildPrivateShareMessage({
          message,
          shareText: privateShareText,
          shareUrl: privateShareUrl,
        }),
      });
      if (result.heldForReview) {
        setShareError(t('moderation.chat.sendHeldMessage'));
        setInAppSharing(false);
        return;
      }
      if (!result.message) throw new Error('Share message was not sent');
      ToastService.show({
        type: 'success',
        message: t('bottomSheet.friendShareSuccess', { username: friend.name }),
      });
      onClose();
    } catch {
      setShareError(t('bottomSheet.friendShareFailed'));
      setInAppSharing(false);
    }
  };

  return (
    <Modal
      title={t('bottomSheet.sharePost')}
      onClose={busy ? () => undefined : onClose}
      labelledBy="share-post-title"
      className="share-post-modal"
    >
      {canReshareInsideWlb && (
        <div
          className={`share-post__composer${
            privacyOpen || showEmojiPicker ? ' share-post__composer--overlay-open' : ''
          }`}
        >
        <div className="share-post__identity">
          <Avatar
            src={profile?.avatar}
            name={profileName}
            username={profile?.userName}
            seed={profile?.userGuid ?? profile?.userId}
            profileUserGuid={profile?.userGuid}
            userId={profile?.userId}
            canonical
            size="md"
          />
          <div>
            <strong>{profileName}</strong>
            <div className="share-post__identity-meta">
              <div className="share-post__privacy-wrap">
                <button
                  type="button"
                  className="share-post__privacy"
                  onClick={() => {
                    setShowEmojiPicker(false);
                    setPrivacyOpen(open => !open);
                  }}
                  aria-haspopup="menu"
                  aria-expanded={privacyOpen}
                  disabled={busy}
                >
                  <PrivacyIcon privacy={privacy} />
                  {privacyLabel}
                  <ChevronDownIcon size={16} />
                </button>
                {privacyOpen && (
                  <div className="share-post__privacy-menu" role="menu">
                    {privacyOptions.map(option => {
                      const isPublic = option === POST_PRIVACY.PUBLIC;
                      const isBuddies = option === POST_PRIVACY.BUDDIES_ONLY;
                      return (
                        <button
                          key={option}
                          type="button"
                          role="menuitemradio"
                          aria-checked={privacy === option}
                          className={privacy === option ? 'selected' : ''}
                          onClick={() => {
                            setPrivacy(option);
                            setPrivacyOpen(false);
                          }}
                        >
                          <PrivacyIcon privacy={option} />
                          <span>
                            <strong>
                              {isPublic
                                ? t('home.privacyInfo.post.publicTitle')
                                : isBuddies
                                  ? t('home.privacyInfo.post.buddiesTitle')
                                  : t('home.privacyInfo.post.onlyMeTitle')}
                            </strong>
                            <small>
                              {isPublic
                                ? t('home.privacyInfo.post.publicDesc')
                                : isBuddies
                                  ? t('home.privacyInfo.post.buddiesDesc')
                                  : t('home.privacyInfo.post.onlyMeDesc')}
                            </small>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {post.groupName && (
                <span>
                  {t('bottomSheet.shareDestinationGroup', {
                    group: post.groupName,
                  })}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="share-post__message-field">
          <textarea
            className="share-post__message"
            value={message}
            onChange={event => setMessage(event.target.value)}
            placeholder={t('bottomSheet.inAppSharePlaceholder')}
            aria-label={t('bottomSheet.inAppSharePlaceholder')}
            maxLength={POST_MAX_CHAR_COUNT}
            rows={2}
            disabled={busy}
          />
          <button
            type="button"
            className="share-post__emoji-toggle"
            onClick={() => {
              setPrivacyOpen(false);
              setShowEmojiPicker(visible => !visible);
            }}
            aria-label={t('comments.emojiPicker')}
            aria-pressed={showEmojiPicker}
            aria-expanded={showEmojiPicker}
            aria-controls="share-post-emoji-picker"
            disabled={busy}
          >
            <SmileIcon size={20} />
          </button>
          {showEmojiPicker && (
            <div
              id="share-post-emoji-picker"
              className="share-post__emoji-panel"
            >
              <EmojiPicker
                onSelect={emoji =>
                  setMessage(current =>
                    `${current}${emoji}`.slice(0, POST_MAX_CHAR_COUNT),
                  )
                }
              />
            </div>
          )}
        </div>
        <FormError
          variant="banner"
          message={shareError}
          className="share-post__error"
        />
        <div className="share-post__submit">
          <Button
            type="button"
            size="sm"
            onClick={() => { handleShareInsideApp().catch(() => undefined); }}
            disabled={busy}
            aria-busy={inAppSharing}
          >
            {t(
              inAppSharing
                ? 'bottomSheet.sharingInsideApp'
                : 'bottomSheet.shareNow',
            )}
          </Button>
        </div>
        <div className="share-post__preview">
          <div className="share-post__preview-author">
            <Avatar
              src={existingLineage ? undefined : post.avatar}
              name={originalAuthor.username}
              username={originalAuthor.username}
              seed={originalAuthor.userGuid ?? originalAuthor.username}
              profileUserGuid={originalAuthor.userGuid}
              size="md"
            />
            <div>
              <strong>{originalAuthor.username}</strong>
              {originalPostFeeling && (
                <span>
                  {t('home.feed.isFeeling', {
                    feeling: originalPostFeeling,
                  })}
                </span>
              )}
            </div>
          </div>
          {originalPostText && <p>{originalPostText}</p>}
          {originalPostVideo ? (
            <video
              className="share-post__preview-media"
              src={originalPostVideo}
              controls
              preload="metadata"
            />
          ) : originalPostImage ? (
            <div className="share-post__preview-media-wrap">
              <img
                className="share-post__preview-media"
                src={originalPostImage}
                alt=""
              />
              {originalPostImages.length > 1 && (
                <span
                  className="share-post__preview-media-count"
                  aria-hidden="true"
                >
                  +{originalPostImages.length - 1}
                </span>
              )}
            </div>
          ) : null}
        </div>
        </div>
      )}

      <section
        className="share-post__inside-wlb"
        aria-labelledby="share-post-buddies-title"
      >
        <header>
          <h3 id="share-post-buddies-title">
            {t('bottomSheet.shareWithBuddies')}
          </h3>
          <button
            type="button"
            onClick={() => { openFriendPicker().catch(() => undefined); }}
            disabled={busy || !messages}
          >
            {t('bottomSheet.seeAll')}
          </button>
        </header>
        <div className="share-post__buddy-row">
          {canReshareInsideWlb && (
            <button
              type="button"
              className="share-post__buddy"
              onClick={() => { openGroupPicker().catch(() => undefined); }}
              disabled={busy || !group}
            >
              <span className="share-post__buddy-group-icon" aria-hidden>
                <GroupsTabIcon size={24} />
              </span>
              <span>{t('bottomSheet.group')}</span>
            </button>
          )}
          {buddiesLoading ? (
            <p className="share-post__buddies-status">{t('common.loading')}</p>
          ) : friendCandidates.length ? (
            friendCandidates.slice(0, 6).map(friend => (
              <button
                key={friend.id}
                type="button"
                className="share-post__buddy"
                onClick={() => {
                  shareWithFriend(friend).catch(() => undefined);
                }}
                disabled={busy}
              >
                <Avatar
                  src={friend.avatar}
                  name={friend.name}
                  username={friend.username}
                  seed={friend.profileUserGuid ?? friend.id}
                  profileUserGuid={friend.profileUserGuid}
                  userId={friend.id}
                  size="md"
                />
                <span title={friend.name}>{friend.name}</span>
              </button>
            ))
          ) : (
            <p className="share-post__buddies-status">
              {t('bottomSheet.noFriends')}
            </p>
          )}
        </div>
      </section>

      {picker && (
        <section className="share-post__picker" aria-label={t(
          picker === 'group'
            ? 'bottomSheet.selectGroup'
            : 'bottomSheet.selectFriend',
        )}>
          <header>
            <strong>
              {t(
                picker === 'group'
                  ? 'bottomSheet.selectGroup'
                  : 'bottomSheet.selectFriend',
              )}
            </strong>
            <button type="button" onClick={() => setPicker(null)}>
              {t('common.buttons.close')}
            </button>
          </header>
          {pickerLoading ? (
            <p>{t('common.loading')}</p>
          ) : picker === 'group' ? (
            group?.myGroups.length ? (
              <div className="share-post__picker-list">
                {group.myGroups.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      handleShareInsideApp(item.id).catch(() => undefined);
                    }}
                    disabled={busy}
                  >
                    <span className="share-post__picker-avatar" aria-hidden>
                      <GroupsTabIcon size={20} />
                    </span>
                    <span>{item.groupName}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p>{t('bottomSheet.noGroups')}</p>
            )
          ) : friendCandidates.length ? (
            <div className="share-post__picker-list">
              {friendCandidates.map(friend => (
                <button
                  key={friend.id}
                  type="button"
                  onClick={() => {
                    shareWithFriend(friend).catch(() => undefined);
                  }}
                  disabled={busy}
                >
                  <Avatar
                    src={friend.avatar}
                    name={friend.name}
                    username={friend.username}
                    seed={friend.profileUserGuid ?? friend.id}
                    profileUserGuid={friend.profileUserGuid}
                    userId={friend.id}
                    size="sm"
                  />
                  <span>{friend.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <p>{t('bottomSheet.noFriends')}</p>
          )}
        </section>
      )}

      <div className="share-post__divider">
        <span>{t('bottomSheet.moreWaysToShare')}</span>
      </div>

      <div className="share-post__options-wrap">
      <div
        ref={shareOptionsRef}
        className="share-post__options"
        onScroll={() => {
          const options = shareOptionsRef.current;
          if (!options) return;
          const remaining = options.scrollWidth - options.clientWidth - options.scrollLeft;
          setCanScrollShareOptionsLeft(options.scrollLeft > 4);
          setCanScrollShareOptionsRight(remaining > 4);
        }}
      >
        <button
          type="button"
          className="share-post__option share-post__option--copy"
          onClick={copyLink}
          disabled={busy}
        >
          <span className="share-post__icon" aria-hidden>
            <LinkIcon size={24} />
          </span>
          {t('bottomSheet.copyLink')}
        </button>
        <a
          className="share-post__option share-post__option--whatsapp"
          href={externalLinks.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="share-post__icon" aria-hidden>
            <MessagesIcon size={24} />
          </span>
          {t('bottomSheet.whatsApp')}
        </a>
        <button
          type="button"
          className="share-post__option share-post__option--instagram"
          onClick={() => { openNativeShare().catch(() => undefined); }}
          disabled={busy}
        >
          <span className="share-post__icon" aria-hidden>
            <InstagramIcon size={24} />
          </span>
          {t('bottomSheet.instagram')}
        </button>
        <a
          className="share-post__option share-post__option--facebook"
          href={externalLinks.facebook}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="share-post__icon" aria-hidden>
            <FacebookIcon size={24} />
          </span>
          {t('bottomSheet.facebook')}
        </a>
        <a
          className="share-post__option share-post__option--x"
          href={externalLinks.x}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="share-post__icon" aria-hidden>
            <XIcon size={24} />
          </span>
          {t('bottomSheet.x')}
        </a>
        <a
          className="share-post__option share-post__option--email"
          href={externalLinks.email}
        >
          <span className="share-post__icon" aria-hidden>
            <MailIcon size={24} />
          </span>
          {t('bottomSheet.email')}
        </a>
        <button
          type="button"
          className="share-post__option share-post__option--native"
          onClick={() => { openNativeShare().catch(() => undefined); }}
          disabled={busy}
        >
          <span className="share-post__icon" aria-hidden>
            <ShareIcon size={24} />
          </span>
          {t('bottomSheet.seeAll')}
        </button>
      </div>
      {canScrollShareOptionsLeft && (
        <button
          type="button"
          className="share-post__options-nav share-post__options-nav--previous"
          onClick={() => {
            shareOptionsRef.current?.scrollBy({
              left: -Math.max(240, shareOptionsRef.current.clientWidth * 0.7),
              behavior: 'smooth',
            });
          }}
          aria-label={t('bottomSheet.previousShareOptions')}
        >
          <ChevronLeftIcon size={24} />
        </button>
      )}
      {canScrollShareOptionsRight && (
        <button
          type="button"
          className="share-post__options-nav share-post__options-nav--next"
          onClick={() => {
            shareOptionsRef.current?.scrollBy({
              left: Math.max(240, shareOptionsRef.current.clientWidth * 0.7),
              behavior: 'smooth',
            });
          }}
          aria-label={t('bottomSheet.moreShareOptions')}
        >
          <ChevronRightIcon size={24} />
        </button>
      )}
      </div>
    </Modal>
  );
}
